import { ChangeDetectorRef, Component, Inject, OnDestroy, OnInit, PLATFORM_ID } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { combineLatest, Subject, Subscription } from 'rxjs';
import { filter, map, take, takeUntil } from 'rxjs/operators';
import { notNullOrUndefined } from '../../common/utils/not-null-or-undefined';
import { Page, StoreSite, WebPresenceConfigData } from '../../common/generated-types';
import { NGXLogger } from 'ngx-logger';
import { parseMaybeJsonList, parseMaybeJsonValue } from '../../common/utils/safe-json-parser';
import { CollectionGroupData, LayoutOptionData, NavMenuOption, SiteStyleData } from '../../common/interfaces';
import { StateService } from '../../core/providers/state/state.service';
import { CanonicalService } from '../../core/providers/canonical/canonical.service';
import { isPlatformBrowser, ViewportScroller } from '@angular/common';
import { stripHtml } from '../../common/utils/strip-html';
import { Meta, Title } from '@angular/platform-browser';
import { replaceHostname } from '../../common/utils/url-handlers';
import { getRoundedClass, computeLayoutStyles } from '../../common/utils/styles-helper';
import { WAIT_FOR_NAVIGATION_INTERVAL, WAIT_FOR_NAVIGATION_TIMEOUT } from '../../common/constants';
import { CarouselArrowType, SiteStyleType } from '../../common/enums';
import { StructuredDataService } from '../../core/providers/structured-data/structured-data.service';
import { AssetPreviewPipe } from '../../shared/pipes/asset-preview.pipe';
import { navigateByNavigationItem } from '../../common/utils/navigation-helpers';

@Component({
  selector: 'vsf-collection-page',
  templateUrl: './collection-page.component.html',
  styleUrls: ['./collection-page.component.scss'],
})
export class CollectionPageComponent implements OnInit, OnDestroy {
  private destroy$ = new Subject<void>();
  storeInfo: StoreSite;
  identifier: string;
  baseUrl = '';
  sellerChannelId: string;
  withHeaderMessage: boolean = true;
  private stateSub: Subscription;
  isMobile: boolean | null = null;
  shopHost: string;
  currentCanonicalURL: string | null = null;
  collectionPage: Page;
  collection: any;
  previewPipe = new AssetPreviewPipe();
  layoutOrder: LayoutOptionData[] = [];
  navigationMenu: NavMenuOption[] = [];
  webPresenceConfig: WebPresenceConfigData | null = null;
  pagecomponentList: any[] = [];
  pageComponentIndex: number[] = [];
  h1PageHeadingIndex: number = -1;
  h1CustomText: string = '';
  h1StyleMode: string = 'auto';
  h1CustomStyles: { [key: string]: string } = {};
  currentCollectionProducts: any[] = [];
  private receivedItemListElements: any[] = [];

  collectionListForShopByGroupList: CollectionGroupData[];

  showFixedBanner: boolean = false;
  isHeaderFloating: boolean = false;
  showTopNav: boolean = true;

  waitForNavigationInterval = WAIT_FOR_NAVIGATION_INTERVAL;
  waitForNavigationTimeout = WAIT_FOR_NAVIGATION_TIMEOUT;
  pdpAssetsArrowType: CarouselArrowType = CarouselArrowType.LargeChevron;

  constructor(@Inject(PLATFORM_ID) private platformId: object,
              private route: ActivatedRoute,
              private canonicalService: CanonicalService,
              private router: Router,
              private titleService: Title,
              private metaService: Meta,
              private structuredDataService: StructuredDataService,
              private viewportScroller: ViewportScroller,
              private changeDetectorRef: ChangeDetectorRef,
              private stateService: StateService,
              private logger: NGXLogger) {}

  async ngOnInit() {
    const isMobile$ = this.stateService.select(state => state.isMobile);
    const storeInfo$ = this.stateService.select(state => state.storeInfo);
    const baseUrl$ = this.stateService.select(state => state.storePath);
    const shopHost$ = this.stateService.select(state => state.shopHost);
    const hasTopMessage$ = this.stateService.select(state => state.hasTopMessage).pipe(takeUntil(this.destroy$), filter(notNullOrUndefined));
    this.stateSub = this.stateService.select(state => ({
      isHeaderFloating: state.isHeaderFloating,
    })).subscribe(({ isHeaderFloating }) => {
      if(isHeaderFloating !== this.isHeaderFloating) {
          this.isHeaderFloating = isHeaderFloating;
          this.changeDetectorRef.markForCheck();
      }
    });
    await combineLatest([isMobile$, storeInfo$, baseUrl$, shopHost$, hasTopMessage$])
    .pipe(take(1)).subscribe(([isMobile, storeInfo, baseUrl, shopHost, hasTopMessage]) => {
        this.isMobile = isMobile;
        this.storeInfo = storeInfo;
        this.baseUrl = baseUrl;
        this.shopHost = shopHost;
        this.sellerChannelId = this.storeInfo?.sellerChannel?.id || '';
        if(this.withHeaderMessage !== hasTopMessage) {
          this.withHeaderMessage = hasTopMessage;
        }
        const siteStyles = parseMaybeJsonValue<SiteStyleData>(this.storeInfo?.siteStyles, this.logger);
        this.pdpAssetsArrowType = siteStyles?.pdpAssetsArrowType || CarouselArrowType.LargeChevron;
    });

    const collectionSlug$ = this.route.paramMap.pipe(
      map(paramMap => paramMap.get('slug')),
      takeUntil(this.destroy$),
      filter(notNullOrUndefined),
    );
    collectionSlug$.subscribe(() => {
      this.collectionPage = this.route.snapshot.data['collectionPage'];
      this.collection = this.collectionPage?.collectionInfo;
      if (isPlatformBrowser(this.platformId)) {
        this.viewportScroller.scrollToPosition([0, 0]);
      }
      this.pagecomponentList = this.collectionPage?.pageComponentList || [];
      this.layoutOrder = this.pagecomponentList.map((pc:any) => {
          const layoutOption =  { ...pc.layoutOption } as LayoutOptionData;
          if (layoutOption) {
            // Compute desktop width percentage
            layoutOption.widthPercentage = layoutOption.width && layoutOption.width > 0
              ? `${layoutOption.width * 100}%`
              : '100%';
            // Compute mobile width percentage
            layoutOption.mobileWidthPercentage = layoutOption.mobileWidth && layoutOption.mobileWidth > 0
              ? `${layoutOption.mobileWidth * 100}%`
              : '100%';
            // Ensure margin properties are numbers (fallback to 0 if undefined)
            layoutOption.topMargin = layoutOption.topMargin || 0;
            layoutOption.bottomMargin = layoutOption.bottomMargin || 0;
            layoutOption.mobileTopMargin = layoutOption.mobileTopMargin || 0;
            layoutOption.mobileBottomMargin = layoutOption.mobileBottomMargin || 0;
            layoutOption.horizontalPadding = layoutOption.horizontalPadding || 0;
            layoutOption.mobileHorizontalPadding = layoutOption.mobileHorizontalPadding || 0;
            layoutOption.topPadding = layoutOption.topPadding || 0;
            layoutOption.bottomPadding = layoutOption.bottomPadding || 0;
            layoutOption.mobileTopPadding = layoutOption.mobileTopPadding || 0;
            layoutOption.mobileBottomPadding = layoutOption.mobileBottomPadding || 0;
            if (this.isMobile) {
              layoutOption.linePadding = `${layoutOption.mobileTopPadding}px ${layoutOption.mobileHorizontalPadding}px
                  ${layoutOption.mobileBottomPadding}px`;
            } else {
              layoutOption.linePadding = `${layoutOption.topPadding}px ${layoutOption.horizontalPadding}px
                  ${layoutOption.bottomPadding}px`;
            }
            const activeRounded = this.isMobile
              ? (layoutOption.mobileSectionRoundedCorners ?? layoutOption.sectionRoundedCorners)
              : layoutOption.sectionRoundedCorners;
            layoutOption.roundedClass = getRoundedClass(activeRounded);
          }
          return layoutOption;
      })
      .filter(notNullOrUndefined);
      this.navigationMenu = parseMaybeJsonList<NavMenuOption>(this.collectionPage?.navigationMenu, this.logger);

      this.webPresenceConfig = this.collectionPage?.webPresence || null;
      this.computeH1Heading();
      if(isPlatformBrowser(this.platformId)) {
        window.addEventListener('scroll', () => {
          const scrolled = window.scrollY || document.documentElement.scrollTop;
          let showTopNav = true;
          if (scrolled > 150) {
              showTopNav = false;
          }
          if(showTopNav !== this.showTopNav) {
              this.showTopNav = showTopNav;
          }
        });
        this.route.queryParams.subscribe((params) => {
          const section = params['section'];
          if (section) {
            this.scrollToSectionAfterLoad(section);
            this.router.navigate([], {
              queryParams: { section: null },
              queryParamsHandling: 'merge'
            });
          }
        });
      }
      this.computeLayoutStyles();
      const canonicalURL = `/gp/${this.collection?.slug.toLowerCase()}`;
      this.canonicalService.setCanonicalURL(`${this.shopHost}${canonicalURL}`);
      this.currentCanonicalURL = canonicalURL;
      this.updateCollectionMetadata(this.collection);
      this.structuredDataService.replaceOne('breadcrumbs', this.buildBreadcrumbsSchema());
      this.changeDetectorRef.detectChanges();
    });
  }

  ngOnDestroy() {
    if (this.stateSub) {
      this.stateSub.unsubscribe();
    }
    if (this.currentCanonicalURL) {
      this.canonicalService.removeCanonicalURL();
      this.currentCanonicalURL = null;
    }
    this.removeMetadata();
    this.structuredDataService.remove('collection');
    this.structuredDataService.remove('breadcrumbs');
    this.destroy$.next();
    this.destroy$.complete();
  }

  scrollToSection(item: NavMenuOption): void {
    navigateByNavigationItem(this.router, item, '/');
  }

  scrollToSectionAfterLoad(sectionId: string): void {
    const interval = setInterval(() => {
      const targetEl = document.getElementById(sectionId);
      if (targetEl) {
        targetEl.scrollIntoView({ behavior: 'smooth' });
        clearInterval(interval);
      }
    }, this.waitForNavigationInterval);
    setTimeout(() => clearInterval(interval), this.waitForNavigationTimeout);
  }

  onItemListSchemaReady(elements: any[]): void {
    this.receivedItemListElements = elements;
    if (this.collection) {
      this.structuredDataService.replaceOne('collection', this.buildCollectionJsonLd());
    }
  }

  private buildCollectionJsonLd(): object {
      const data = {
          '@context': 'https://schema.org',
          '@type': 'CollectionPage',
          "name": this.collection?.name || this.collection?.slug,
          "image": `${this.storeInfo?.storeUrl}${this.previewPipe.transform(this.collection?.featuredAsset, 'small', 'jpeg')}`,
          "description": this.collection?.description || '',
          "url": `${this.storeInfo?.storeUrl}/gp/${this.collection?.slug}`,
          "mainEntity": {
              '@type': 'ItemList',
              "itemListElement": this.receivedItemListElements,
              "numberOfItems": this.receivedItemListElements.length || 0,
          },
          "isPartOf": {
              "@type": "WebSite",
              "url": `${this.storeInfo?.storeUrl}`,
              "name": this.storeInfo?.name,
          }
      };
      return data;
  }

  private buildBreadcrumbsSchema(): object {
    const data = {
          "@context": "https://schema.org",
          "@type": "BreadcrumbList",
          "itemListElement": [
              {
                  "@type": "ListItem",
                  "position": 1,
                  "name": "Home",
                  "item": `${this.storeInfo?.storeUrl}`
              },
              {
                  "@type": "ListItem",
                  "position": 2,
                  "name": this.collection?.name || this.collection?.slug,
                  "item": `${this.storeInfo?.storeUrl}/gp/${this.collection?.slug}`
              }
          ]
      }
      return data;
  }

  private updateCollectionMetadata(collection: any): void {
      if (!collection) return;
      let seoTitle = '';
      let seoDescription = '';
      let ogTitle = '';
      let ogDescription = '';
      let imageUrl = '';
      if(!this.webPresenceConfig || this.webPresenceConfig?.useBuiltInSeo) {
          seoTitle = collection.name;
          seoDescription = collection.description;
          ogTitle = collection.name;
          ogDescription = collection.description;
          imageUrl = this.previewPipe.transform(collection.featuredAsset, 600, 315);
      } else {
          const titleHtml = this.webPresenceConfig?.title || collection.name;
          const descriptionHtml = this.webPresenceConfig.description || '';
          seoTitle = stripHtml(titleHtml, this.platformId);
          seoDescription = stripHtml(descriptionHtml, this.platformId);
          ogTitle = this.webPresenceConfig.openGraphTitleSameAsSeoTitle ? seoTitle :
              (this.webPresenceConfig.openGraphTitle ?
                  stripHtml(this.webPresenceConfig.openGraphTitle, this.platformId) : seoTitle);
          ogDescription = this.webPresenceConfig.openGraphDescriptionSameAsSeoDescription ? seoDescription :
              (this.webPresenceConfig.openGraphDescription ?
                  stripHtml(this.webPresenceConfig.openGraphDescription, this.platformId) : seoDescription);
          imageUrl = this.webPresenceConfig?.metaAsset?.imageUrl || this.webPresenceConfig?.metaAsset?.preview || '';
      }
      // Set the page title
      this.titleService.setTitle(seoTitle);
      // Set the meta description
      this.metaService.updateTag({ name: 'description', content: seoDescription });
      // Set Open Graph meta tags
      this.metaService.updateTag({ property: 'og:title', content: ogTitle });
      this.metaService.updateTag({ property: 'og:description', content: ogDescription });
      this.metaService.updateTag({ property: 'og:type', content: 'collection' });
      this.metaService.updateTag({ property: 'og:url', content: `${this.shopHost}${this.baseUrl}/gp/${collection.slug}` });
      // Set Twitter Card meta tags
      this.metaService.updateTag({ name: 'twitter:title', content: seoTitle });
      this.metaService.updateTag({ name: 'twitter:description', content: seoDescription });
      // Set image meta tags
      if (imageUrl) {
        this.metaService.updateTag({ name: 'twitter:card', content: 'summary_large_image' });
        this.metaService.updateTag({ property: 'og:image', content: `${this.shopHost}${this.baseUrl}${imageUrl}` });
        this.metaService.updateTag({ property: 'og:image:alt', content: ogTitle });
        this.metaService.updateTag({ name: 'twitter:image', content: `${this.shopHost}${this.baseUrl}${imageUrl}` });
        this.metaService.updateTag({ name: 'twitter:image:alt', content: ogTitle });
      } else {
        this.metaService.updateTag({ name: 'twitter:card', content: 'summary' });
      }
      this.logger.debug(`Updated metadata for collection:${seoTitle}`);
  }

  private removeMetadata() {
      this.titleService.setTitle(`${this.storeInfo?.name? this.storeInfo.name : ''}`);
      this.metaService.removeTag('name="description"');
      this.metaService.removeTag('property="og:title"');
      this.metaService.removeTag('property="og:description"');
      this.metaService.removeTag('property="og:type"');
      this.metaService.removeTag('property="og:url"');
      this.metaService.removeTag('property="og:image"');
      this.metaService.removeTag('name="twitter:card"');
      this.metaService.removeTag('name="twitter:title"');
      this.metaService.removeTag('name="twitter:description"');
      this.metaService.removeTag('name="twitter:image"');
  }

  private computeH1Heading(): void {
    this.h1PageHeadingIndex = -1;
    this.h1CustomText = '';
    this.h1StyleMode = this.webPresenceConfig?.h1StyleMode || 'auto';
    this.h1CustomStyles = this.computeH1Styles();
    const h1Source = this.webPresenceConfig?.h1Source || 'auto';
    if (h1Source === 'none') {
      return;
    }
    if (h1Source === 'custom') {
      this.h1CustomText = this.webPresenceConfig?.h1Text || '';
      return;
    }
    if (this.pagecomponentList?.length > 0) {
      this.h1PageHeadingIndex = this.pagecomponentList.findIndex(
        (pc: any) => pc.title || pc.subtitle || pc.content?.title,
      );
    }
  }

  private computeH1Styles(): { [key: string]: string } {
    if (this.h1StyleMode !== 'custom') {
      return {};
    }
    const styles: { [key: string]: string } = {};
    const fontSize = this.isMobile
      ? this.webPresenceConfig?.h1MobileFontSize
      : this.webPresenceConfig?.h1FontSize;
    if (fontSize) {
      styles['font-size'] = `${fontSize}px`;
    }
    if (this.webPresenceConfig?.h1Color) {
      styles['color'] = this.webPresenceConfig.h1Color;
    }
    if (this.webPresenceConfig?.h1Alignment) {
      styles['text-align'] = this.webPresenceConfig.h1Alignment;
    }
    return styles;
  }

  computeLayoutStyles() {
    computeLayoutStyles(this.layoutOrder, this.isMobile);
  }
}